import { ChangeDetectionStrategy, Component, computed, input, output } from "@angular/core";

import { LktIconComponent } from "../icon/icon.component";
import { LktNotificationItem } from "../../types/notification-item.type";

/**
 * Linha da lista de notificações (DEV-872, CA 03).
 *
 * Presentational puro: recebe o view-model já montado (`LktNotificationItem`) e
 * emite duas intenções — `select` (marca a notificação como lida; não navega —
 * `onItemSelect` no container só chama `markRead`, e `LktNotificationItem` não
 * tem campo de rota) e `ctaSelect` (a ação contextual, quando o tipo tem uma).
 *
 * A frase chega decomposta do backend (`subject` + `message`) e é renderizada
 * declarativamente, sem `[innerHTML]` e sem `DomSanitizer`.
 */
@Component({
  selector: "lkt-notification-item",
  standalone: true,
  imports: [LktIconComponent],
  templateUrl: "./notification-item.component.html",
  styleUrl: "./notification-item.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LktNotificationItemComponent {
  public readonly item = input.required<LktNotificationItem>();
  public readonly dataCy = input<string>("");

  /**
   * `no-output-native` marca `select` como nome de evento nativo. O nome é
   * mantido porque é o verbo do contrato consumido pelo painel e pelos
   * containers; o binding do output tem precedência sobre o listener nativo,
   * logo não há ambiguidade em tempo de execução.
   */
  // eslint-disable-next-line @angular-eslint/no-output-native
  public readonly select = output<void>();
  public readonly ctaSelect = output<void>();

  /**
   * O tom colore o disco por CLASSE, nunca por estilo inline como no protótipo:
   * cor em atributo `style` não sobrevive a tema/whitelabel nem a CSP.
   */
  public readonly dotClass = computed<string>(
    () => `nt-item__dot nt-item__dot--${this.item().tone}`
  );

  /** Sem `dataCy` do consumidor não emite atributo — nada de `"-cta"` órfão. */
  public readonly ctaDataCy = computed<string | undefined>(() =>
    this.dataCy() ? `${this.dataCy()}-cta` : undefined
  );

  public onSelect(): void {
    this.select.emit();
  }

  /**
   * Fix round 1 (DEV-872): não recebe mais `MouseEvent` nem chama
   * `stopPropagation`. Na v1 o CTA era um <button> aninhado dentro do
   * <button> da linha — HTML inválido — e precisava interromper a
   * propagação para não disparar `select` junto. Agora o CTA é um <button>
   * IRMÃO (padrão "stretched link" no SCSS: `.nt-item__main::after` cobre a
   * linha, o CTA fica por cima via `z-index`), então o clique nele nunca
   * borbulha para o botão da linha — testado por remoção: o teste
   * `emite ctaSelect sem emitir select...` continua verde sem a chamada.
   */
  public onCtaSelect(): void {
    this.ctaSelect.emit();
  }
}
